import React, {useEffect, useRef, useState, KeyboardEvent} from 'react';
import Box from '@mui/material/Box';
import type {SxProps, Theme} from '@mui/material/styles';
import {gradients} from '@/styles/tokens';
import styles from './ScrollableTabs.module.scss';

interface ScrollableTabsProps {
    value: number;
    onChange: (value: number) => void;
    items: string[];
    ariaLabel?: string;
    /** sx applied to every tab button; active/selected state is handled by ScrollableTabs.module.scss's own [aria-selected] rule */
    tabSx?: SxProps<Theme>;
}

// Must match the ids hardcoded by CustomTabPanel (`simple-tabpanel-{i}` /
// `simple-tab-{i}`); kept as a constant so the aria linkage can't drift.
const ID_PREFIX = 'simple';

// Single source of truth for the active gradient (text + underline), handed to
// the SCSS module as a CSS custom property so the module owns no brand colour.
const rootStyle = {'--tabs-active-gradient': gradients.primaryActive} as React.CSSProperties;

/**
 * Accessible, horizontally scrollable tab strip (WAI-ARIA tabs pattern with
 * automatic activation). Mirrors MUI's scrollable `Tabs`: overflowing strips get
 * left/right scroll buttons. Panels are rendered separately (see `CustomTabPanel`).
 */
function ScrollableTabs({value, onChange, items, ariaLabel, tabSx}: Readonly<ScrollableTabsProps>) {
    const scrollerRef = useRef<HTMLDivElement>(null);
    const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
    // The tab the strip last scrolled to. Comparing against it (rather than a "has mounted"
    // flag) keeps the effect below a no-op on load even when React runs effects twice in
    // Strict Mode — a mount flag is already true on the second run and let it fire.
    const prevValueRef = useRef(value);
    const [canLeft, setCanLeft] = useState(false);
    const [canRight, setCanRight] = useState(false);

    const updateScrollState = () => {
        const s = scrollerRef.current;
        if (!s) return;
        setCanLeft(s.scrollLeft > 1);
        setCanRight(s.scrollLeft + s.clientWidth < s.scrollWidth - 1);
    };

    // Recompute overflow on mount, on tab-count change, and on container resize.
    useEffect(() => {
        const s = scrollerRef.current;
        if (!s || typeof ResizeObserver === 'undefined') return undefined;
        updateScrollState();
        const ro = new ResizeObserver(updateScrollState);
        ro.observe(s);
        return () => ro.disconnect();
    }, [items.length]);

    // Keep the active tab visible after a tab change on an overflowing strip. Only the
    // strip scrolls, horizontally: `scrollIntoView` would also scroll the page vertically
    // and drag it down to this tab strip.
    useEffect(() => {
        if (prevValueRef.current === value) return;
        prevValueRef.current = value;

        const scroller = scrollerRef.current;
        const tab = tabRefs.current[value];
        if (!scroller || !tab) return;

        const scrollerRect = scroller.getBoundingClientRect();
        const tabRect = tab.getBoundingClientRect();
        if (tabRect.left < scrollerRect.left) {
            scroller.scrollLeft -= scrollerRect.left - tabRect.left;
        } else if (tabRect.right > scrollerRect.right) {
            scroller.scrollLeft += tabRect.right - scrollerRect.right;
        }
    }, [value]);

    const selectAndFocus = (i: number) => {
        const next = (i + items.length) % items.length;
        tabRefs.current[next]?.focus();
        onChange(next);
    };

    const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
        switch (e.key) {
            case 'ArrowRight':
                e.preventDefault();
                selectAndFocus(index + 1);
                break;
            case 'ArrowLeft':
                e.preventDefault();
                selectAndFocus(index - 1);
                break;
            case 'Home':
                e.preventDefault();
                selectAndFocus(0);
                break;
            case 'End':
                e.preventDefault();
                selectAndFocus(items.length - 1);
                break;
            default:
                break;
        }
    };

    const scrollByDir = (dir: number) => {
        const s = scrollerRef.current;
        if (s) s.scrollBy({left: dir * s.clientWidth * 0.7, behavior: 'smooth'});
    };

    const overflowing = canLeft || canRight;

    return (
        <div className={styles.root} style={rootStyle}>
            {overflowing && (
                <button
                    type="button"
                    className={styles.scrollButton}
                    aria-hidden="true"
                    tabIndex={-1}
                    disabled={!canLeft}
                    onClick={() => scrollByDir(-1)}
                >
                    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
                        <path
                            d="M15 18l-6-6 6-6"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </button>
            )}
            <div ref={scrollerRef} role="tablist" aria-label={ariaLabel} className={styles.scroller} onScroll={updateScrollState}>
                {items.map((label, index) => {
                    const selected = value === index;
                    return (
                        <Box
                            component="button"
                            key={index}
                            type="button"
                            role="tab"
                            id={`${ID_PREFIX}-tab-${index}`}
                            aria-selected={selected}
                            aria-controls={`${ID_PREFIX}-tabpanel-${index}`}
                            tabIndex={selected ? 0 : -1}
                            ref={(node: HTMLButtonElement | null) => {
                                tabRefs.current[index] = node;
                            }}
                            className={styles.tab}
                            sx={tabSx}
                            onClick={() => onChange(index)}
                            onKeyDown={(e) => onKeyDown(e, index)}
                        >
                            {label}
                        </Box>
                    );
                })}
            </div>
            {overflowing && (
                <button
                    type="button"
                    className={styles.scrollButton}
                    aria-hidden="true"
                    tabIndex={-1}
                    disabled={!canRight}
                    onClick={() => scrollByDir(1)}
                >
                    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
                        <path
                            d="M9 18l6-6-6-6"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </button>
            )}
        </div>
    );
}

export default ScrollableTabs;
